import { useMemo } from "react";

import { useCartContext } from "../../contexts/CartServiceContext/CartServiceContext";

interface IFormattedCustomization {
	food_supplier_customization_option_id: number;
	quantity: number;
}

interface IFormattedSetItem {
	external_id: string;
	food_supplier_menu_item_id: number;
	quantity: number;
	customizations?: IFormattedCustomization[];
}

type TItemWithCustomizations = {
	food_supplier_customizations?: {
		food_supplier_customization_options?: {
			id: string | number;
			quantity?: number;
		}[];
	}[];
};

function formatCustomizations(
	customizations: TItemWithCustomizations["food_supplier_customizations"]
): IFormattedCustomization[] | undefined {
	if (!customizations) {
		return undefined;
	}

	const formatted = customizations.flatMap(
		(customization) =>
			customization?.food_supplier_customization_options?.map((option) => ({
				food_supplier_customization_option_id: Number(option.id),
				quantity: option.quantity ?? 1,
			})) ?? []
	);

	return formatted.length > 0 ? formatted : undefined;
}

function formatSetItem(setItem: any): IFormattedSetItem {
	return {
		external_id: setItem.external_id,
		food_supplier_menu_item_id: Number(setItem.food_supplier_menu_item_id),
		quantity: setItem.quantity ?? 1,
		customizations: formatCustomizations(setItem.food_supplier_customizations),
	};
}

export const useFormattedCart = () => {
	const { state: cart } = useCartContext((state) => state.cart);

	const formattedCart = useMemo(
		() =>
			cart.map((item, index) => {
				const formattedSetItems = item.food_supplier_set_items?.map(formatSetItem);

				return {
					...item,
					food_supplier_menu_item_id: Number(item.id),
					item_order_id: index.toString(),
					customizations: formatCustomizations(item.food_supplier_customizations),
					food_supplier_set_items:
						formattedSetItems && formattedSetItems.length > 0 ? formattedSetItems : undefined,
				};
			}),
		[cart]
	);

	return formattedCart;
};
